import React, { useEffect, useRef, useState } from "react";
import { prepareMessage } from "@/lib/api";
import { toast } from "sonner";

const MAX = 500;

/**
 * MessageModal — a deeper layer of THE VOID.
 * No card, no frame. Just the question, the writing, the gesture.
 * The black hole beneath breathes through a translucent dim so the room stays alive.
 */
export default function MessageModal({ open, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const taRef = useRef(null);

  useEffect(() => {
    if (open) {
      setText("");
      setConfirming(false);
      // Give the entrance animation time to breathe before focusing
      setTimeout(() => taRef.current && taRef.current.focus(), 900);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const remaining = MAX - text.length;
  const canProceed = text.trim().length > 0 && text.length <= MAX && !loading;

  const submit = async () => {
    if (!canProceed) return;
    setLoading(true);
    try {
      const { checkout_url } = await prepareMessage(text.trim(), null);
      window.location.href = checkout_url;
    } catch (e) {
      console.error(e);
      toast.error("The darkness didn't respond. Try again.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 modal-fade-in"
      data-testid="message-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        background: "rgba(0,0,0,0.88)",
        backdropFilter: "blur(22px) saturate(120%)",
        WebkitBackdropFilter: "blur(22px) saturate(120%)",
      }}
    >
      <div
        className="w-full h-full flex items-center justify-center px-6 sm:px-10 relative z-10"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-2xl mx-auto">
          {!confirming ? (
            <>
              <h2
                className="font-serif text-white/95 text-center reveal-slow"
                style={{
                  fontSize: "clamp(28px, 4.2vw, 48px)",
                  lineHeight: 1.05,
                  fontVariationSettings: '"opsz" 144, "SOFT" 0, "WONK" 0',
                  letterSpacing: "-0.015em",
                }}
                data-testid="modal-title"
              >
                What would you never say out loud?
              </h2>

              <div className="mt-14 reveal-slow reveal-delay-1">
                <textarea
                  ref={taRef}
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, MAX))}
                  placeholder="Write it here. Throw it into the dark."
                  rows={4}
                  className="void-textarea text-center"
                  data-testid="message-textarea"
                  aria-label="Your message to the void"
                />
                <div className="mt-2 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                <div
                  className="mt-3 font-mono-void text-white/25 text-center"
                  style={{ fontSize: "10px", letterSpacing: "0.42em", textTransform: "uppercase" }}
                  data-testid="char-counter"
                >
                  {remaining} left
                </div>
              </div>

              <div className="mt-14 flex justify-center reveal-slow reveal-delay-2">
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  disabled={!canProceed}
                  className="void-cta"
                  data-testid="modal-continue"
                >
                  Send it into the dark · $1
                </button>
              </div>

              {/* Accessible, invisible close for keyboard users; visible label removed */}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="modal-cancel"
                className="sr-only-focusable"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <h2
                className="font-serif text-white/95 text-center reveal-slow"
                style={{
                  fontSize: "clamp(26px, 3.6vw, 40px)",
                  lineHeight: 1.15,
                  fontVariationSettings: '"opsz" 144, "SOFT" 0, "WONK" 0',
                  letterSpacing: "-0.015em",
                }}
                data-testid="confirm-title"
              >
                Something happens at{" "}
                <span className="font-mono-void text-white">1,000,000</span>.
              </h2>

              <div
                className="mt-12 font-serif-body text-white/60 border-l border-white/10 pl-6 py-1 mx-auto max-w-md reveal-slow reveal-delay-1"
                style={{ fontSize: "clamp(17px, 1.9vw, 21px)", lineHeight: 1.45 }}
                data-testid="confirm-preview"
              >
                {text}
              </div>

              <div className="mt-14 flex flex-col items-center gap-6 reveal-slow reveal-delay-2">
                <button
                  onClick={submit}
                  disabled={loading}
                  className="void-cta"
                  data-testid="confirm-pay"
                  aria-label="Send this message into the void for one US dollar"
                >
                  {loading ? "Opening the dark…" : "Send it · $1"}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  className="font-ui text-white/35 hover:text-white/85 transition-colors duration-500"
                  style={{ fontSize: "10px", letterSpacing: "0.42em" }}
                  data-testid="confirm-back"
                >
                  Take it back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
